/**
 * ContentReviewModal - Review and optionally edit AI-generated content
 * before confirming and proceeding to parsing.
 *
 * Parses AI-generated JSON to display a formatted article view.
 * Falls back to raw text display if JSON parsing fails.
 * Edit mode shows raw JSON for advanced editing.
 *
 * NOT closable - user must confirm or go back.
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ModalShell from './ModalShell';

interface ContentReviewModalProps {
  generatedText: string;
  onConfirm: (text: string) => void;
  onBack: () => void;
}

// Parsed JSON structure from AI content generator
interface ParsedContent {
  content_meta?: {
    topic?: string;
    cefr_level?: string;
    tone?: string;
  };
  article?: {
    title_en?: string;
    title_native?: string;
    paragraphs?: Array<{
      id?: string;
      text_en?: string;
      text_native?: string;
      sentiment?: string;
    }>;
  };
  vocabulary_drill?: Array<{
    word?: string;
    definition?: string;
    part_of_speech?: string;
  }>;
}

function tryParseJSON(text: string): ParsedContent | null {
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object' && parsed.article) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function countArticleWords(parsed: ParsedContent): number {
  if (!parsed.article?.paragraphs) return 0;
  const allText = parsed.article.paragraphs
    .map((p) => p.text_en || '')
    .join(' ')
    .trim();
  if (!allText) return 0;
  return allText.split(/\s+/).length;
}

function countRawWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

const ContentReviewModal: React.FC<ContentReviewModalProps> = ({
  generatedText,
  onConfirm,
  onBack,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(generatedText);
  const [showTranslation, setShowTranslation] = useState(false);

  const currentText = isEditing ? editedText : generatedText;

  const parsed = useMemo(() => tryParseJSON(currentText), [currentText]);

  const stats = useMemo(() => {
    if (parsed) {
      const words = countArticleWords(parsed);
      const vocabCount = parsed.vocabulary_drill?.length || 0;
      return { words, vocabCount };
    }
    return { words: countRawWords(currentText), vocabCount: 0 };
  }, [parsed, currentText]);

  const handleToggleEdit = () => {
    if (!isEditing) {
      setEditedText(generatedText);
    }
    setIsEditing(!isEditing);
  };

  const handleConfirm = () => {
    onConfirm(currentText.trim());
  };

  const renderFormattedContent = (content: ParsedContent) => {
    const { article, content_meta, vocabulary_drill } = content;
    if (!article) return null;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Title */}
        {article.title_en && (
          <div>
            <h3 style={{
              fontSize: '1.15rem',
              fontWeight: 700,
              color: 'var(--text-primary, #1a1a2e)',
              margin: 0,
              lineHeight: 1.4,
            }}>
              {article.title_en}
            </h3>
            {showTranslation && article.title_native && (
              <p style={{
                fontSize: '0.9rem',
                color: 'var(--text-secondary, #6b7280)',
                margin: '4px 0 0',
              }}>
                {article.title_native}
              </p>
            )}
          </div>
        )}

        {/* Meta tags */}
        {content_meta && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {content_meta.cefr_level && (
              <span style={{
                padding: '2px 10px',
                borderRadius: '12px',
                fontSize: '0.78rem',
                fontWeight: 600,
                background: '#dbeafe',
                color: '#1e40af',
              }}>
                {content_meta.cefr_level}
              </span>
            )}
            {content_meta.tone && (
              <span style={{
                padding: '2px 10px',
                borderRadius: '12px',
                fontSize: '0.78rem',
                fontWeight: 500,
                background: '#f3e8ff',
                color: '#7c3aed',
              }}>
                {content_meta.tone}
              </span>
            )}
          </div>
        )}

        {/* Paragraphs */}
        {article.paragraphs?.map((para, idx) => (
          <div key={para.id || idx} style={{
            padding: '12px 16px',
            borderRadius: '8px',
            background: 'var(--bg-primary, #fff)',
            border: '1px solid var(--border-color, #e5e7eb)',
          }}>
            <p style={{
              margin: 0,
              fontSize: '0.95rem',
              lineHeight: 1.8,
              color: 'var(--text-primary, #1a1a2e)',
              whiteSpace: 'pre-wrap',
            }}>
              {para.text_en}
            </p>
            {showTranslation && para.text_native && (
              <p style={{
                margin: '8px 0 0',
                fontSize: '0.85rem',
                lineHeight: 1.6,
                color: 'var(--text-secondary, #6b7280)',
                borderTop: '1px solid var(--border-color, #e5e7eb)',
                paddingTop: '8px',
                whiteSpace: 'pre-wrap',
              }}>
                {para.text_native}
              </p>
            )}
          </div>
        ))}

        {/* Vocabulary summary */}
        {vocabulary_drill && vocabulary_drill.length > 0 && (
          <div>
            <h4 style={{
              fontSize: '0.9rem',
              fontWeight: 600,
              color: 'var(--text-primary, #1a1a2e)',
              margin: '0 0 8px',
            }}>
              {t('creator:workflow.resourceModal.vocabularyPreview', {
                count: vocabulary_drill.length,
                defaultValue: `Key Vocabulary (${vocabulary_drill.length})`,
              })}
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {vocabulary_drill.map((v, i) => (
                <span
                  key={i}
                  title={`${v.part_of_speech ? `(${v.part_of_speech}) ` : ''}${v.definition || ''}`}
                  style={{
                    padding: '3px 10px',
                    borderRadius: '14px',
                    fontSize: '0.82rem',
                    fontWeight: 500,
                    background: '#ecfdf5',
                    color: '#065f46',
                    cursor: 'default',
                  }}
                >
                  {v.word}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <ModalShell
      title={t('creator:workflow.resourceModal.reviewTitle')}
      onBack={onBack}
      onNext={handleConfirm}
      nextLabel={t('creator:workflow.resourceModal.confirmContent')}
      nextDisabled={!currentText.trim()}
      showClose={false}
      wide
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          {/* Translation toggle - only show if parsed content has translations */}
          {parsed && !isEditing && (
            <button
              type="button"
              onClick={() => setShowTranslation(!showTranslation)}
              style={{
                background: showTranslation
                  ? 'var(--primary-color, #6366f1)'
                  : 'var(--bg-hover, #f3f4f6)',
                color: showTranslation ? '#fff' : 'var(--text-secondary, #6b7280)',
                border: 'none',
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: '0.85rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 8l6 6" /><path d="M4 14l6-6 2-3" /><path d="M2 5h12" /><path d="M7 2h1" />
                <path d="M22 22l-5-10-5 10" /><path d="M14 18h6" />
              </svg>
              {t('creator:workflow.resourceModal.showTranslation', { defaultValue: 'Translation' })}
            </button>
          )}

          {/* Edit toggle */}
          <button
            type="button"
            onClick={handleToggleEdit}
            style={{
              background: isEditing
                ? 'var(--primary-color, #6366f1)'
                : 'var(--bg-hover, #f3f4f6)',
              color: isEditing ? '#fff' : 'var(--text-secondary, #6b7280)',
              border: 'none',
              padding: '6px 14px',
              borderRadius: '6px',
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            {isEditing ? t('common:editing') : t('common:edit')}
          </button>
        </div>

        {/* Content display / edit area */}
        {isEditing ? (
          <textarea
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            style={{
              width: '100%',
              minHeight: '300px',
              padding: '16px',
              borderRadius: '10px',
              border: '1px solid var(--primary-color, #6366f1)',
              fontSize: '0.9rem',
              lineHeight: '1.7',
              resize: 'vertical',
              fontFamily: "'SF Mono', 'Fira Code', 'Fira Mono', 'Roboto Mono', monospace",
              color: 'var(--text-primary, #1a1a2e)',
              background: 'var(--bg-primary, #fff)',
              outline: 'none',
              boxSizing: 'border-box',
              boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)',
            }}
          />
        ) : parsed ? (
          <div
            style={{
              width: '100%',
              minHeight: '200px',
              maxHeight: '400px',
              overflowY: 'auto',
              padding: '16px',
              borderRadius: '10px',
              border: '1px solid var(--border-color, #e5e7eb)',
              background: 'var(--bg-secondary, #f9fafb)',
              boxSizing: 'border-box',
            }}
          >
            {renderFormattedContent(parsed)}
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              minHeight: '200px',
              maxHeight: '400px',
              overflowY: 'auto',
              padding: '16px',
              borderRadius: '10px',
              border: '1px solid var(--border-color, #e5e7eb)',
              fontSize: '0.9rem',
              lineHeight: '1.7',
              fontFamily: 'inherit',
              color: 'var(--text-primary, #1a1a2e)',
              background: 'var(--bg-secondary, #f9fafb)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              boxSizing: 'border-box',
            }}
          >
            {generatedText || t('creator:workflow.resourceModal.noContentGenerated')}
          </div>
        )}

        {/* Stats */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '16px',
            fontSize: '0.82rem',
            color: 'var(--text-secondary, #6b7280)',
          }}
        >
          <span>
            {t('creator:workflow.resourceModal.wordCount', { count: stats.words })}
          </span>
          {stats.vocabCount > 0 && (
            <span>
              {t('creator:workflow.resourceModal.vocabCount', {
                count: stats.vocabCount,
                defaultValue: `${stats.vocabCount} vocabulary`,
              })}
            </span>
          )}
        </div>
      </div>
    </ModalShell>
  );
};

export default ContentReviewModal;
